// Placeholder content for Pablo Marquez Executive Portfolio.
// Replace fields here without touching components.

export const portfolio = {
  name: "Pablo Marquez",
  title: "Equestrian Industry Executive",
  subtitle: "Founder · Business Developer · Sports Innovator",
  positioning:
    "Building the future of equestrian sport through media, technology and commercial innovation.",
  intro: `For more than two decades, I have worked across every layer of the equestrian industry — from international competition and elite events to media ventures, league development and technology-driven businesses.\n\nMy work has consistently centred on expanding audiences, improving commercial sustainability and exploring new opportunities for growth within the equestrian industry.`,
  aboutHeadline:
    "More than two decades at the intersection of sport, business and innovation.",
  bio: `Pablo Marquez is an equestrian industry executive, entrepreneur and international show jumping athlete with over twenty years of experience building, managing and commercialising projects within the global equestrian sector.`,
  bioLong: `Throughout his career, he has operated across multiple areas of the industry, including elite sport, event management, sponsorship, hospitality, media rights, broadcasting, league development and sports technology. This unique combination of operational, commercial and sporting experience has allowed him to understand the equestrian ecosystem from every perspective.\n\nAs Executive Director and Board Member of Madrid Horse Week, Pablo helped transform one of Europe's leading FEI World Cup events into a premium sports and entertainment platform. As Founder of the National Equestrian League in the United States, he developed an innovative team-based competition model designed to professionalise the sport through media, sponsorship and franchise ownership. He later founded Ryde TV, a sports technology and media venture combining artificial intelligence, automated production and digital distribution to make professional broadcasting accessible to every equestrian event.\n\nAlongside his executive career, Pablo remains actively involved in the sport as an international show jumping rider, horse professional and advisor, maintaining a direct connection to the realities and opportunities facing athletes, owners, organisers and commercial partners.\n\nHis work is driven by a belief that equestrian sport possesses enormous untapped potential and that innovation, technology and modern business models will play a critical role in its future growth.`,
  hero: {
    backgroundImage:
      "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/qfdxqd6u_Imagen%2027-5-22%20a%20las%2014.12.jpeg",
    profileImage:
      "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/2b7b2bp8_IMG_6033.jpeg",
  },
  stats: [
    { value: 20, suffix: "+", label: "Years in Equestrian Industry" },
    { value: 8, suffix: "+", label: "Years Leading Madrid Horse Week" },
    { text: "Founder", label: "National Equestrian League" },
    { text: "Founder", label: "Ryde TV" },
  ],
  timeline: [
    {
      year: "2022 — 2023",
      role: "Founder",
      org: "Ryde TV",
      desc: "Founded a sports technology and media venture combining AI, automated production and digital distribution to make professional broadcasting accessible to every equestrian event.",
    },
    {
      year: "2018 — 2020",
      role: "Founder & CEO",
      org: "National Equestrian League",
      desc: "Designed and launched a franchise-based league structured around FEI regulations and broadcast-first formats.",
    },
    {
      year: "2012 — 2021",
      role: "Executive Director",
      org: "Madrid Horse Week",
      desc: "Led the FEI World Cup property: sponsorship, hospitality, ticketing, media and government relations.",
    },
    {
      year: "2002",
      role: "Show Jumping Athlete",
      org: "International CSI Circuit",
      desc: "Two-decade involvement in show jumping as rider, horseman and equestrian professional — the foundation of every project that followed.",
    },
  ],
  projects: [
    {
      id: "national-equestrian-league",
      name: "National Equestrian League",
      role: "Founder & CEO",
      years: "2018 — 2020",
      mission:
        "Build a franchise-based national league with broadcast-first formats and a North American expansion roadmap.",
      achievements: [
        "Designed team franchise model and league economics",
        "FEI-aligned regulatory framework",
        "TV-native competition format optimised for broadcast",
        "North America expansion blueprint",
      ],
      image:
        "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/jzfjz6at_794920D9-9597-417F-9CA4-94C490ACCB4B.png",
      imageFit: "contain",
      tags: ["League", "Franchise", "Broadcast", "Expansion"],
    },
    {
      id: "madrid-horse-week",
      name: "Madrid Horse Week",
      role: "Executive Director · Board Member",
      years: "2012 — 2021",
      mission:
        "Lead one of Europe's premier FEI World Cup events across commercial, operational and institutional fronts.",
      achievements: [
        "8+ consecutive editions delivered at FEI World Cup standard",
        "100+ sponsors negotiated across Tier 1 and Tier 2 brands",
        "Government and federation relations across Spain and Europe",
        "Premium hospitality, ticketing and media architecture",
      ],
      image:
        "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/50umk0f6_mhw4.jpg",
      tags: ["FEI World Cup", "Sponsorship", "Media", "Hospitality"],
    },
    {
      id: "ridetv",
      name: "Ryde TV",
      role: "Founder",
      years: "2022 — 2023",
      mission:
        "A sports technology and media venture making professional broadcasting accessible to every equestrian event through AI, automation and digital distribution.",
      achievements: [
        "First AI-driven automated broadcasting concept focused exclusively on equestrian sport",
        "Scalable across show jumping, dressage, polo and racing",
        "New sponsorship and commercial activation opportunities via digital content",
        "Positioned as a sports technology venture, not a streaming app",
      ],
      image:
        "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/xoxxv42z_2CD9B4F1-5629-4CB2-9EDC-1319B4C819CA.png",
      tags: ["AI", "Sports Tech", "Media", "Founder"],
    },
    {
      id: "professional-show-jumper",
      name: "Professional Show Jumper",
      role: "Athlete · Horseman · Entrepreneur",
      years: "2002 — Present",
      mission:
        "Competitor across CSI 2*–4* events — first-hand operational understanding of athletes, venues and federations.",
      achievements: [
        "International CSI circuit experience",
        "Athlete-economics fluency",
        "Federation and venue operations exposure",
        "Foundation for executive career in equestrian sport",
      ],
      image:
        "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/l7qv66m2_IMG_5687.JPG",
      tags: ["Show Jumping", "FEI", "Athlete"],
    },
  ],
  madridHorseWeek: {
    title: "Madrid Horse Week",
    eyebrow: "Experience · FEI World Cup",
    headline:
      "Eight consecutive editions of an FEI World Cup property — commercially, institutionally and operationally.",
    metrics: [
      { value: "8+", label: "Editions delivered" },
      { value: "100+", label: "Sponsor negotiations" },
      { value: "FEI", label: "World Cup status" },
      { value: "EU", label: "Government relations" },
    ],
    pillars: [
      "Executive Director",
      "Board Member",
      "Sponsorship",
      "Hospitality",
      "Ticketing",
      "Media",
      "Government Relations",
      "FEI World Cup Event",
    ],
    image:
      "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/50umk0f6_mhw4.jpg",
  },
  league: {
    title: "National Equestrian League",
    eyebrow: "Experience · Founder & CEO",
    headline:
      "Reimagining professional show jumping through teams, media and franchise ownership.",
    pillars: [
      "Founder & CEO",
      "League Concept",
      "Team Franchise Model",
      "FEI Regulations",
      "Media Strategy",
      "TV Format",
      "North America Expansion",
    ],
    metrics: [
      { value: "v1", label: "League architecture" },
      { value: "FEI", label: "Compliant regulations" },
      { value: "NA", label: "Target market" },
      { value: "Franchise", label: "Ownership model" },
    ],
    image:
      "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/jzfjz6at_794920D9-9597-417F-9CA4-94C490ACCB4B.png",
    imageFit: "contain",
  },
  ridetv: {
    title: "Ryde TV",
    eyebrow: "Experience · Founder",
    headline:
      "A sports technology and media venture making professional broadcasting accessible to every equestrian event.",
    pillars: [
      "Founder",
      "Sports Technology",
      "Artificial Intelligence",
      "Automated Production",
      "Digital Distribution",
      "Commercial Scalability",
      "Innovation",
    ],
    metrics: [
      { value: "AI", label: "Driven production" },
      { value: "Global", label: "Streaming vision" },
      { value: "Multi", label: "Discipline platform" },
      { value: "Founder", label: "Led venture" },
    ],
    image:
      "https://customer-assets.emergentagent.com/job_marquez-portfolio-1/artifacts/xoxxv42z_2CD9B4F1-5629-4CB2-9EDC-1319B4C819CA.png",
  },
  // Credibility strip — top-tier institutions / events / ventures
  // genuinely associated with Pablo's executive experience.
  // Keep this list short and curated (5-7 max). Edit freely.
  credibility: {
    eyebrow: "Trusted across",
    items: [
      "FEI",
      "Madrid Horse Week",
      "IFEMA · Madrid",
      "National Equestrian League",
      "Ryde TV",
    ],
  },

  skills: [
    "Business Development",
    "Strategic Partnerships",
    "Sports Commercialisation",
    "Sponsorship Sales",
    "Media Rights",
    "Event Management",
    "League Development",
    "Leadership",
    "Negotiation",
    "Product Strategy",
  ],
  expertise: [
    { title: "FEI Competitions", desc: "World Cup, CSI 2*–5* event operations." },
    { title: "Show Jumping", desc: "Athlete, official and commercial perspective." },
    { title: "League Development", desc: "Franchise models, regulations, formats." },
    { title: "Equestrian Media", desc: "Rights, distribution and editorial." },
    { title: "Horse Trading", desc: "Cross-border commercial frameworks." },
    { title: "Venue Operations", desc: "Logistics, hospitality, broadcast." },
    { title: "Hospitality", desc: "Premium and ultra-premium guest experiences." },
    { title: "Sponsorship", desc: "Tier 1 brand activation and integration." },
    { title: "Broadcasting", desc: "OTT, linear and automated production." },
  ],
  contact: {
    email: "contact@equestrianpartners.net",
    whatsapp: "+34 622 11 35 67",
    phone: "+34 622 11 35 67",
    whatsappUrl: "https://wa.me/34622113567",
  },
};

export default portfolio;
